import React, { useEffect, useState } from "react";
//#region  types
import { SelectionItem } from "models/utilTypes/selectionTypes";
import { WintSuperkollAPIModelWintSalaryDeviations } from "wint-api-client/dist/types";
import { SalaryDeviationFormTypes } from "models/formTypes";
import { GraphConstants } from "utils/constants";
import { ChartEnums } from "models/enums";
import { TranslationKeys } from "translations";
import {
  WintDeviationGroupByDeviationTypeChartKey,
  WintDeviationGroupByPersonChartKey,
} from "utils/constants/salaryDeviationConstants";
//#endregion
//#region  components
import { useFilterContext, ViewPanel, Chart, ToggleButtonGroup, Skeletons } from "components/common";
import CategorySelector from "../_partials/categorySelector";
import { useWintSalaryDeviationService } from "serviceHooks";
import { ErrorBoundary } from "components/error";
import { Grid } from "@mui/material";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";
import { useSalaryPermissions } from "components/salary";

const DeviationDistributionChart: React.FC = () => {
  const { reportToApiFilter, getValidDeviationCategoriesSelectionItems } = useWintSalaryDeviationService();
  const { filter: pageFilter } = useFilterContext<SalaryDeviationFormTypes.WintSalaryDeviationReportFilter>();
  const [chartFilter, setChartFilter] = useState<
    WintSuperkollAPIModelWintSalaryDeviations.DeviationReportFilter | undefined
  >(undefined);
  const { isSalaryAdmin } = useSalaryPermissions();

  const buildDeviationFilter = (
    baseFilter: SalaryDeviationFormTypes.WintSalaryDeviationReportFilter,
    selectedCategoryId: WintSuperkollAPIModelWintSalaryDeviations.SalaryDeviationTypeCategory,
    groupingOption: WintSuperkollAPIModelWintSalaryDeviations.PieChartGroupingOption
  ): WintSuperkollAPIModelWintSalaryDeviations.DeviationReportFilter => {
    const apiFilter = reportToApiFilter(baseFilter);
    return {
      ...apiFilter,
      deviationCategories: [selectedCategoryId],
      pieChartGroupingOption: groupingOption,
    };
  };

  const availableCategories =
    pageFilter?.categoryTypes && pageFilter.categoryTypes.length > 0
      ? pageFilter.categoryTypes
      : getValidDeviationCategoriesSelectionItems();

  const selctedChartCategory = (chartFilter?.deviationCategories?.[0] ??
    availableCategories[0].id) as WintSuperkollAPIModelWintSalaryDeviations.SalaryDeviationTypeCategory;

  const selectedChartGroupingOption =
    chartFilter?.pieChartGroupingOption ??
    (isSalaryAdmin
      ? WintSuperkollAPIModelWintSalaryDeviations.PieChartGroupingOption.ByUser
      : WintSuperkollAPIModelWintSalaryDeviations.PieChartGroupingOption.ByDeviationType);

  const selectedCategoryItem =
    availableCategories?.find((c) => c.id === selctedChartCategory) ?? (availableCategories && availableCategories[0]);

  const groupingLabel =
    selectedChartGroupingOption === WintSuperkollAPIModelWintSalaryDeviations.PieChartGroupingOption.ByDeviationType
      ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Type)
      : LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Person);

  const chartTitleText = selectedCategoryItem?.value
    ? `${selectedCategoryItem.value} per ${groupingLabel.toLowerCase()}`
    : "";

  const dataUnit =
    selctedChartCategory === WintSuperkollAPIModelWintSalaryDeviations.SalaryDeviationTypeCategory.Absence ||
    selctedChartCategory === WintSuperkollAPIModelWintSalaryDeviations.SalaryDeviationTypeCategory.WorkedHours
      ? "h"
      : GraphConstants.DefaultDataUnit;

  const baseFilter = (pageFilter ?? {}) as SalaryDeviationFormTypes.WintSalaryDeviationReportFilter;

  const onCategoryChange = (category: SelectionItem) => {
    setChartFilter(
      buildDeviationFilter(
        baseFilter,
        category.id as WintSuperkollAPIModelWintSalaryDeviations.SalaryDeviationTypeCategory,
        selectedChartGroupingOption
      )
    );
  };

  useEffect(() => {
    setChartFilter(buildDeviationFilter(baseFilter, selctedChartCategory, selectedChartGroupingOption));
  }, [pageFilter]);

  const onGroupOptionClicked = (groupingOption: WintSuperkollAPIModelWintSalaryDeviations.PieChartGroupingOption) => {
    setChartFilter(buildDeviationFilter(baseFilter, selctedChartCategory, groupingOption));
  };

  return (
    <ViewPanel
      title={
        <CategorySelector
          onCategoryChange={onCategoryChange}
          title={LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.SalaryDeviation.WintTodoSalaryBreakdownTitle
          )}
          availableCategories={availableCategories}
          selectedCategory={selctedChartCategory}
        />
      }
      appearance="sm"
      gridSize="md"
      isCanvasContent
      topRightContent={
        <Grid item className={cssExports.toggleContainer}>
          <ToggleButtonGroup
            initialSelectionKey={
              isSalaryAdmin ? WintDeviationGroupByPersonChartKey : WintDeviationGroupByDeviationTypeChartKey
            }
            buttons={[
              {
                buttonKey: WintDeviationGroupByPersonChartKey,
                children: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Person),
                onClick: () =>
                  onGroupOptionClicked(WintSuperkollAPIModelWintSalaryDeviations.PieChartGroupingOption.ByUser),
                disabled: !isSalaryAdmin,
              },
              {
                buttonKey: WintDeviationGroupByDeviationTypeChartKey,
                children: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.DeviationType),
                onClick: () =>
                  onGroupOptionClicked(
                    WintSuperkollAPIModelWintSalaryDeviations.PieChartGroupingOption.ByDeviationType
                  ),
              },
            ]}
          />
        </Grid>
      }
    >
      <ErrorBoundary>
        {chartFilter ? (
          <Chart
            chartAPIKey={GraphConstants.ChartKeys.WintSalaryDeviationDistributionChart}
            chartTitle={chartTitleText}
            filter={chartFilter}
            doughnutChartLegendType={ChartEnums.LegendType.WITH_VALUES}
            doughnutChartColorPalette={ChartEnums.DoughnutChartColorPalette.DEFAULT}
            dataUnit={dataUnit}
          />
        ) : (
          <Skeletons.WidgetSkeleton />
        )}
      </ErrorBoundary>
    </ViewPanel>
  );
};

export default DeviationDistributionChart;
